import type {ReactNode} from 'react'
import type {OverviewUser} from '../../../../routes/route-payload-types'
import {PullRequestState} from '../../../../types'
import {AbleToMerge, shouldShowAbleToMerge} from '../mergeability/AbleToMerge'
import {ApprovableChecks} from '../mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from '../mergeability/AwaitingApprovalChecks'
import {shouldShowDeleteHeadBranch, DeleteHeadBranch} from '../mergeability/DeleteHeadBranch'
import {shouldShowDraftPR, DraftPR} from '../mergeability/DraftPR'
import {FailingChecks} from '../mergeability/FailingChecks'
import {shouldShowMergeBlocked, MergeBlocked} from '../mergeability/MergeBlocked'
import {shouldShowMergeConflicts, MergeConflicts} from '../mergeability/MergeConflicts'
import {PendingChecks} from '../mergeability/PendingChecks'
import {shouldShowReadyToMerge, ReadyToMerge} from '../mergeability/ReadyToMerge'
import {decideChecksItem} from '../mergeability/shared/checks-item-decider'
import {selectFailedRequiredChecks} from '../mergeability/use-failed-checks-data'
import {useMergeabilityData, type MergeabilityData} from '../mergeability/use-mergeability-data'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {useOverviewRouteData} from '../../data/use-overview-route-data'

type AuthorForYouActionInput = {
  mergeabilityData: MergeabilityData | null
  pullRequestState: PullRequestState
  user: OverviewUser | undefined
}

type LazyAction = {
  action: ReactNode
  matches: () => boolean
}

export function useAuthorOrAssigneeDecider() {
  const mergeabilityData = useMergeabilityData()
  const pullRequestState = usePullRequestState()
  const {user} = useOverviewRouteData()

  return getAuthorForYouAction({
    mergeabilityData,
    pullRequestState,
    user,
  })
}

export function getAuthorForYouAction({mergeabilityData, pullRequestState, user}: AuthorForYouActionInput): ReactNode {
  const decisionTree: LazyAction[] = [
    {action: <DeleteHeadBranch variant="embedded" />, matches: () => shouldShowDeleteHeadBranch(mergeabilityData)},
    {action: <DraftPR variant="embedded" />, matches: () => shouldShowDraftPR(pullRequestState, user)},
    {
      action: <FailingChecks variant="embedded" />,
      matches: () => Boolean(mergeabilityData && selectFailedRequiredChecks(mergeabilityData.statusChecks).length),
    },
    {
      action: <ApprovableChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'approvable',
    },
    {
      action: <AwaitingApprovalChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'awaiting-approval',
    },
    {action: <MergeConflicts variant="embedded" />, matches: () => shouldShowMergeConflicts(mergeabilityData)},
    {
      action: <PendingChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'pending',
    },
    {action: <MergeBlocked variant="embedded" />, matches: () => shouldShowMergeBlocked(mergeabilityData)},
    {action: <ReadyToMerge variant="embedded" />, matches: () => shouldShowReadyToMerge(mergeabilityData)},
    {action: <AbleToMerge variant="embedded" />, matches: () => shouldShowAbleToMerge(mergeabilityData)},
  ]

  return decisionTree.find(decision => decision.matches())?.action ?? null
}
